import { codeableConcept } from '@seen/core/codeableConcept'
import type { DateString } from '@seen/core/dates'
import { invariant } from '@seen/core/invariant'
import { ExtendedObservation } from '@seen/core/models/observation'
import { Button } from '@seen/ui'
import { IconCalendar } from '@tabler/icons-react'
import { type JSX, useEffect, useRef, useState } from 'react'
import { answerChipClass } from '#src/components/rich-text-editor/ObservationNode/observationChip.ts'
import { ObservationDatePicker } from '#src/components/rich-text-editor/ObservationNode/ObservationDatePicker.tsx'
import { ObservationNodeFrame } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeFrame.tsx'
import { CHOOSE_PARTICIPANT } from '#src/components/rich-text-editor/ObservationNode/observationSubject.ts'
import type {
  ObservationCaptureViewProps,
  ObservationNodeAttributes,
} from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { useNodeRecord } from '#src/components/rich-text-editor/ObservationNode/useNodeRecord.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'

// An unanswered date or timestamp code. Picking a day records it; a
// timestamp code records the day alone, which FHIR allows.
export function ObservationNodeDateInput({
  capture,
  ...props
}: ObservationCaptureViewProps): JSX.Element {
  const attributes = props.node.attrs as ObservationNodeAttributes
  invariant(attributes.valueKind, 'valueKind is required')

  const { participant, noParticipant } = capture
  const practitioner = useCurrentPractitioner()
  const { create } = useNodeRecord()
  const isReadOnly = useIsReadOnly()
  const [saving, setSaving] = useState(false)
  const disabled = saving || isReadOnly || noParticipant || !capture.editable

  const buttonRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (attributes.autofocus && buttonRef.current) {
      buttonRef.current.focus()
      props.updateAttributes({ autofocus: false })
    }
  }, [attributes.autofocus, props])

  const record = async (value: DateString): Promise<void> => {
    setSaving(true)
    try {
      await create(
        ExtendedObservation.fromValueDateTime({
          value,
          code: codeableConcept({
            code: attributes.code,
            system: attributes.system,
            display: attributes.codingDisplay,
          }),
          participant,
          practitioner: practitioner.reference,
          status: capture.recordStatus,
        })
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <ObservationNodeFrame
      attributes={attributes}
      updateAttributes={props.updateAttributes}
      blockedReason={noParticipant ? CHOOSE_PARTICIPANT : undefined}
      participant={participant}
    >
      <ObservationDatePicker value={null} onPick={record}>
        <Button
          ref={buttonRef}
          variant="unstyled"
          size="unstyled"
          disabled={disabled}
          className={answerChipClass()}
        >
          Pick date…
          <IconCalendar className="size-3" />
        </Button>
      </ObservationDatePicker>
    </ObservationNodeFrame>
  )
}
